import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { DocumentHeader } from "../../components/DocumentHeader";
import { SignaturePad } from "../../components/SignaturePad";
import { DocumentClosureBar } from "../../components/DocumentClosureBar";
import { PhoneDiagram } from "../../components/PhoneDiagram";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { toast } from "sonner";
import { Printer, Save, Mail, X, Circle, Undo2, Eraser, Hash, Tag, PenLine, ArrowLeft, PackagePlus } from "lucide-react";
import { DocumentImagesGallery } from "../../components/DocumentImagesGallery";
import { AjouterAuStockDialog } from "../../components/AjouterAuStockDialog";
import { DiagnosticTestsEditor } from "../../components/DiagnosticTestsEditor";

const ETAT_OPTIONS = [
  { key: "fonctionnel", label: "Fonctionnel" },
  { key: "deconnexion_google_icloud", label: "Déconnexion Google / iCloud" },
  { key: "debloque_operateur", label: "Débloqué tout opérateur" },
];

// Limites alignées sur ce qui tient réellement dans les encadrés du PDF (voir
// REMARQUES_MAX_LENGTH / PIECE_A_REMPLACER_MAX_LENGTH côté backend,
// pdf_utils.py) — au-delà, l'API refuse l'enregistrement.
const REMARQUES_MAX_LENGTH = 240;
const PIECE_A_REMPLACER_MAX_LENGTH = 50;

const DISCLAIMER = "Le vendeur déclare être le propriétaire du produit repris et certifie l'avoir acquis de manière légale. Il autorise la boutique à revendre, réparer, recycler ou détruire le produit à sa discrétion. En cas de blocage ultérieur du produit (compte, mot de passe, opérateur), le vendeur s'engage à débloquer immédiatement le produit ou à rembourser intégralement la somme perçue lors de la reprise.";

// Champs d'identité du vendeur exigés par le LIVRE DE POLICE des achats
// d'occasion (art. L321-7 code de la sécurité intérieure) — obligatoires pour
// pouvoir CLÔTURER la fiche (signature posée). Doit rester synchronisé avec
// `livre_police_utils.REQUIRED_FIELDS` côté backend, qui reste la seule
// vérification qui fasse foi.
const PIECE_IDENTITE_OPTIONS = [
  { value: "CNI", label: "Carte nationale d'identité" },
  { value: "Passeport", label: "Passeport" },
  { value: "Titre de séjour", label: "Titre de séjour" },
  { value: "Permis de conduire", label: "Permis de conduire" },
];
const LIVRE_POLICE_REQUIRED_FIELDS = [
  "client_nom", "client_adresse", "vendeur_date_naissance", "vendeur_lieu_naissance",
  "piece_identite_type", "piece_identite_numero", "piece_identite_delivree_le", "modele",
];

export default function RepriseFormPage() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const sigRef = useRef(null);
  const mentionSigRef = useRef(null);
  const [shop, setShop] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailMessage, setMailMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [markType, setMarkType] = useState("casse");

  // --- Sélection manuelle du numéro (au lieu de la génération automatique) ---
  // L'utilisateur ne saisit QUE la partie séquentielle (ex. "774") : le
  // préfixe/millésime affichés (ex. "REP-2026-") restent ceux configurés
  // pour la boutique, jamais modifiables depuis ce champ. Le numéro complet
  // (ex. "REP-2026-0774") n'est composé que côté serveur, à l'enregistrement.
  const [manualNumero, setManualNumero] = useState(false);
  const [numeroPattern, setNumeroPattern] = useState(null); // { preview_prefix, digits }
  const [numeroSeq, setNumeroSeq] = useState("");
  const [numeroCheck, setNumeroCheck] = useState(null); // { disponible, numero } | null = pas encore vérifié
  const [checkingNumero, setCheckingNumero] = useState(false);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stagedImages, setStagedImages] = useState([]);
  const [data, setData] = useState({
    numero: "", client_id: searchParams.get("client_id") || null,
    client_nom: searchParams.get("client_nom") || "", client_tel: searchParams.get("client_tel") || "",
    client_email: searchParams.get("client_email") || "", client_adresse: searchParams.get("client_adresse") || "",
    modele: "", capacite: "", imei: "", etat_produit: {}, tests: {}, batterie_pourcentage: "",
    remarques: "", defauts_marks: [], piece_a_remplacer: "", offre_rachat: 0, mention_bon_pour_accord: "",
    vendeur_date_naissance: "", vendeur_lieu_naissance: "", piece_identite_type: "",
    piece_identite_numero: "", piece_identite_delivree_le: "", piece_identite_lieu_delivrance: "",
  });

  useEffect(() => {
    (async () => {
      const shopsRes = await api.get("/shops");
      const myShop = shopsRes.data.find((s) => s.id === user.effective_shop_id);
      if (!myShop && isNew) {
        toast.error("Aucune boutique n'est assignée à votre compte. Contactez un administrateur.");
      }
      setShop(myShop || shopsRes.data[0]);
      if (!isNew) {
        const { data: item } = await api.get(`/reprises/${id}`);
        setData(item);
      }
    })();
  }, [id]);

  // Vérification en direct (avec anti-rebond) de la disponibilité du numéro
  // saisi manuellement — un simple retour visuel avant la validation finale,
  // qui revérifie de toute façon (seule vérification qui fasse foi en cas de
  // saisies concurrentes par deux personnes en même temps).
  useEffect(() => {
    if (!isNew || !manualNumero || !numeroSeq.trim()) {
      setNumeroCheck(null);
      return;
    }
    setCheckingNumero(true);
    const timeout = setTimeout(async () => {
      try {
        const { data: res } = await api.get("/reprises/numero-disponible", { params: { seq: numeroSeq.trim() } });
        setNumeroCheck(res);
      } catch {
        setNumeroCheck(null);
      } finally {
        setCheckingNumero(false);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [numeroSeq, manualNumero, isNew]);

  const enableManualNumero = async () => {
    setManualNumero(true);
    setNumeroSeq("");
    setNumeroCheck(null);
    if (!numeroPattern) {
      try {
        const { data: pattern } = await api.get("/reprises/numero-pattern");
        setNumeroPattern(pattern);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        setManualNumero(false);
      }
    }
  };

  const today = new Date().toLocaleDateString("fr-FR");
  const isCloturee = data.status === "cloture";
  const openTicketRachat = () => {
    window.open(`${api.defaults.baseURL}/reprises/${data.id}/ticket-rachat`, "_blank");
  };
  const payerRachat = async () => {
    if (!window.confirm(`Sortir ${Number(data.offre_rachat).toFixed(2)} € en espèces de la caisse pour ce rachat ?`)) return;
    try {
      const res = await api.post(`/reprises/${data.id}/payer-rachat`, {});
      setData((d) => ({ ...d, ...res.data }));
      toast.success("Sortie d'espèces enregistrée");
      openTicketRachat();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const hasEditPerm = data.is_shared_to_me
    ? data.share_mode === "write"
    : (isNew ? hasPerm(user, "reprise", "create") : hasPerm(user, "reprise", "edit"));
  const canEdit = hasEditPerm && !isCloturee;
  // Livre de police (art. L321-7 code de la sécurité intérieure) : tant que
  // l'identité du vendeur n'est pas complète, on empêche la signature côté
  // interface plutôt que de laisser l'utilisateur découvrir le refus au
  // moment de l'enregistrement — le backend revérifie de toute façon (seule
  // vérification qui fasse foi).
  const identityMissingFields = LIVRE_POLICE_REQUIRED_FIELDS.filter((f) => !String(data[f] || "").trim());
  const identityComplete = identityMissingFields.length === 0;

  const toggleEtat = (key) => setData({ ...data, etat_produit: { ...data.etat_produit, [key]: !data.etat_produit[key] } });
  const addMark = (mark) => setData({ ...data, defauts_marks: [...data.defauts_marks, mark] });
  const removeMark = (idx) => setData({ ...data, defauts_marks: data.defauts_marks.filter((_, i) => i !== idx) });
  const undoLastMark = () => setData({ ...data, defauts_marks: data.defauts_marks.slice(0, -1) });
  const clearMarks = () => setData({ ...data, defauts_marks: [] });

  // --- Sauvegarde ---
  // `persist` est la fonction commune au bouton "Enregistrer" classique et au
  // nouveau flux "Enregistrer et signer" (voir plus bas) : elle construit le
  // payload à partir de l'état courant du formulaire, avec la possibilité de
  // forcer certains champs (`overrides`, utilisé pour injecter la mention et
  // la signature une fois validées dans la fenêtre de signature). Les pads de
  // signature ne sont plus affichés en permanence sur la page (voir plus bas)
  // : quand leurs refs ne sont pas montées, `.current` vaut simplement
  // `undefined` et la valeur déjà enregistrée (`data.signature_data`) est
  // conservée telle quelle.
  const buildPayload = (overrides = {}) => {
    const signature_data = overrides.signature_data ?? (sigRef.current?.toDataURL() || data.signature_data || "");
    const mention_bon_pour_accord = overrides.mention_bon_pour_accord ?? (mentionSigRef.current?.toDataURL() || data.mention_bon_pour_accord || "");
    return {
      ...data,
      // Numéro : uniquement pertinent à la création (voir ci-dessous) — sur
      // une mise à jour, on renvoie toujours le numéro déjà attribué tel
      // quel. Envoyer "" ici déclenchait systématiquement l'erreur serveur
      // « Le numéro ne peut pas être vide » dès la 2e sauvegarde d'une fiche
      // existante (bug corrigé : auparavant ce champ valait toujours "" en
      // dehors de la création, y compris pour une simple modification).
      numero: isNew ? (manualNumero ? numeroSeq.trim() : "") : (data.numero || ""),
      signature_data,
      mention_bon_pour_accord,
      batterie_pourcentage: data.batterie_pourcentage === "" || data.batterie_pourcentage === null ? null : Number(data.batterie_pourcentage),
      offre_rachat: data.offre_rachat === "" || data.offre_rachat === null || Number.isNaN(Number(data.offre_rachat)) ? 0 : Number(data.offre_rachat),
    };
  };

  const canPersist = () => {
    if (isNew && !shop) {
      toast.error("Impossible de créer : aucune boutique assignée à votre compte.");
      return false;
    }
    if (isNew && manualNumero && !numeroSeq.trim()) {
      toast.error("Saisissez un numéro, ou revenez à la génération automatique");
      return false;
    }
    if (isNew && manualNumero && numeroCheck?.disponible === false) {
      toast.error("Ce numéro est déjà utilisé — choisissez-en un autre");
      return false;
    }
    return true;
  };

  // Renvoie l'identifiant du document créé/mis à jour, ou null si la
  // sauvegarde n'a pas pu avoir lieu (validation ou erreur serveur) — utilisé
  // par le flux de signature pour savoir s'il peut enchaîner sur l'étape
  // suivante.
  const persist = async (overrides = {}, { refresh = false } = {}) => {
    if (!canPersist()) return null;
    const payload = buildPayload(overrides);
    try {
      if (isNew) {
        const { data: created } = await api.post("/reprises", { ...payload, shop_id: shop.id });
        // Bug corrigé : il fallait obligatoirement enregistrer avant de
        // pouvoir ajouter la moindre photo. Les photos choisies AVANT la
        // création (mode brouillon, voir DocumentImagesGallery) sont
        // désormais envoyées automatiquement ici, juste après la création
        // réelle du document.
        for (const file of stagedImages) {
          const fd = new FormData();
          fd.append("file", file);
          try {
            await api.post(`/reprises/${created.id}/images`, fd);
          } catch (imgErr) {
            toast.error(`Une image n'a pas pu être enregistrée : ${formatApiError(imgErr.response?.data?.detail)}`);
          }
        }
        toast.success(`Reprise ${created.numero} créée`);
        navigate(`/reprises/${created.id}`);
        return created.id;
      }
      const { data: updated } = await api.patch(`/reprises/${id}`, payload);
      toast.success("Reprise mise à jour");
      if (refresh) setData(updated);
      return id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    }
  };

  const save = () => persist();

  // --- Signature (nouveau flux "Enregistrer et signer") ---
  // Les champs de signature ne sont plus visibles directement sur la fiche :
  // ce bouton enregistre d'abord le formulaire tel quel, puis ouvre une
  // fenêtre en deux étapes (mention manuscrite "Bon pour accord", puis
  // signature) avant de clôturer le document.
  const [signOpen, setSignOpen] = useState(false);
  const [signStep, setSignStep] = useState("mention"); // "mention" | "signature"
  const [pendingMention, setPendingMention] = useState("");
  const [signBusy, setSignBusy] = useState(false);

  const openSignFlow = async () => {
    if (!identityComplete) {
      toast.error("Complétez l'identité du vendeur (livre de police) avant de signer.");
      return;
    }
    setSignBusy(true);
    const savedId = await persist();
    setSignBusy(false);
    if (!savedId) return;
    setPendingMention("");
    setSignStep("mention");
    setSignOpen(true);
  };

  const validateMentionStep = () => {
    const value = mentionSigRef.current?.toDataURL() || "";
    if (!value) {
      toast.error("Faites écrire « Bon pour accord » dans le cadre avant de valider.");
      return;
    }
    setPendingMention(value);
    setSignStep("signature");
  };

  const finalizeSignature = async () => {
    const value = sigRef.current?.toDataURL() || "";
    if (!value) {
      toast.error("La signature est requise avant de clôturer.");
      return;
    }
    setSignBusy(true);
    const savedId = await persist(
      { mention_bon_pour_accord: pendingMention, signature_data: value },
      { refresh: true },
    );
    setSignBusy(false);
    if (savedId) {
      setSignOpen(false);
      toast.success("Fiche signée et clôturée");
    }
  };

  const closeSignDialog = (open) => {
    if (!open) setSignStep("mention");
    setSignOpen(open);
  };

  const openMailDialog = () => {
    setMailTo(data.client_email || "");
    setMailSubject(`Votre fiche de reprise ${data.numero || ""} - ${shop?.nom || ""}`);
    setMailMessage(`Bonjour ${data.client_nom || ""},\n\nVeuillez trouver ci-joint votre fiche de reprise ${data.numero || ""}.\n\nCordialement,\n${shop?.nom || ""}`);
    setMailOpen(true);
  };

  const sendMail = async () => {
    if (!mailTo) { toast.error("Saisissez l'adresse e-mail du client"); return; }
    setSending(true);
    try {
      const res = await api.post(`/reprises/${id}/send-email`, { to_email: mailTo, subject: mailSubject, message: mailMessage });
      toast.success(`Fiche envoyée à ${res.data.sent_to}`);
      setMailOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout title="Fiche de reprise téléphone">
      <div className="a4-sheet max-w-4xl mx-auto p-4 sm:p-8 md:p-12 rounded-md">
        <DocumentHeader
          shop={shop}
          numero={
            manualNumero
              ? (numeroCheck?.numero || (numeroSeq ? `${numeroPattern?.preview_prefix || ""}${numeroSeq}` : "(numéro à saisir)"))
              : (data.numero || "(auto)")
          }
        />
        {isNew && (
          <div className="flex flex-wrap items-center gap-2 -mt-3 mb-4" data-testid="reprise-numero-manuel-bloc">
            {!manualNumero ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5"
                data-testid="reprise-select-numero-button"
                onClick={enableManualNumero}
              >
                <Hash size={13} /> Sélectionner un numéro
              </Button>
            ) : (
              <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                <span className="text-sm font-mono text-muted-foreground whitespace-nowrap" data-testid="reprise-numero-prefixe">
                  {numeroPattern?.preview_prefix || "…"}
                </span>
                <Input
                  data-testid="reprise-numero-manuel-input"
                  placeholder={numeroPattern ? "0".repeat(numeroPattern.digits) : "0000"}
                  value={numeroSeq}
                  onChange={(e) => setNumeroSeq(e.target.value.replace(/[^0-9]/g, ""))}
                  inputMode="numeric"
                  className="max-w-[140px] font-mono"
                />
                {checkingNumero && <span className="text-xs text-muted-foreground">Vérification...</span>}
                {!checkingNumero && numeroCheck?.disponible === true && (
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400" data-testid="reprise-numero-disponible">
                    {numeroCheck.numero} disponible
                  </span>
                )}
                {!checkingNumero && numeroCheck?.disponible === false && (
                  <span className="text-xs font-medium text-destructive" data-testid="reprise-numero-indisponible">
                    {numeroCheck.numero} déjà utilisé
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-muted-foreground"
                  data-testid="reprise-numero-auto-button"
                  onClick={() => { setManualNumero(false); setNumeroSeq(""); setNumeroCheck(null); }}
                >
                  Revenir à la génération automatique
                </Button>
              </div>
            )}
          </div>
        )}
        {data.is_shared_to_me && (
          <p className="text-xs text-center text-muted-foreground mb-4" data-testid="reprise-shared-banner">
            Document partagé par {data.shared_by_label} — {data.share_mode === "write" ? "lecture / écriture" : "lecture seule"}
          </p>
        )}
        <h2 className="font-heading text-xl font-bold text-center mb-6">FICHE DE REPRISE TÉLÉPHONE</h2>

        <DocumentClosureBar
          module="reprises"
          id={isNew ? null : id}
          data={data}
          canEdit={hasEditPerm}
          canDelete={hasPerm(user, "reprise", "delete")}
          onUpdated={setData}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm">
          <div><span className="font-semibold">Vendeur:</span> {data.vendeur_nom || `${user.prenom} ${user.nom}`}</div>
          <div><span className="font-semibold">Date:</span> {data.date || today}</div>
          <Input data-testid="rep-client-nom" disabled={!canEdit} placeholder="Nom client" value={data.client_nom} onChange={(e) => setData({ ...data, client_nom: e.target.value })} />
          <Input data-testid="rep-client-tel" disabled={!canEdit} placeholder="Téléphone" value={data.client_tel} onChange={(e) => setData({ ...data, client_tel: e.target.value })} />
          <Input data-testid="rep-client-email" disabled={!canEdit} placeholder="Email" value={data.client_email} onChange={(e) => setData({ ...data, client_email: e.target.value })} />
          <Input data-testid="rep-client-adresse" disabled={!canEdit} placeholder="Adresse" value={data.client_adresse} onChange={(e) => setData({ ...data, client_adresse: e.target.value })} />
          <Input data-testid="rep-modele" disabled={!canEdit} placeholder="Modèle téléphone" value={data.modele} onChange={(e) => setData({ ...data, modele: e.target.value })} />
          <Input data-testid="rep-capacite" disabled={!canEdit} placeholder="Capacité" value={data.capacite} onChange={(e) => setData({ ...data, capacite: e.target.value })} />
          <Input data-testid="rep-imei" disabled={!canEdit} placeholder="IMEI" value={data.imei} onChange={(e) => setData({ ...data, imei: e.target.value })} />
        </div>

        <div className="border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 rounded-md p-4 mb-6">
          <p className="font-semibold text-sm mb-1">Identité du vendeur — livre de police</p>
          <p className="text-xs text-muted-foreground mb-3">
            Obligatoire (art. L321-7 du code de la sécurité intérieure) pour tout rachat d'objet d'occasion : à
            renseigner à partir de la pièce d'identité présentée par le vendeur, avant de faire signer la fiche.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              data-testid="rep-vendeur-date-naissance"
              disabled={!canEdit}
              placeholder="Date de naissance (JJ/MM/AAAA)"
              value={data.vendeur_date_naissance}
              onChange={(e) => setData({ ...data, vendeur_date_naissance: e.target.value })}
            />
            <Input
              data-testid="rep-vendeur-lieu-naissance"
              disabled={!canEdit}
              placeholder="Lieu de naissance"
              value={data.vendeur_lieu_naissance}
              onChange={(e) => setData({ ...data, vendeur_lieu_naissance: e.target.value })}
            />
            <Select
              value={data.piece_identite_type || ""}
              onValueChange={(v) => setData({ ...data, piece_identite_type: v })}
              disabled={!canEdit}
            >
              <SelectTrigger data-testid="rep-piece-identite-type"><SelectValue placeholder="Nature de la pièce d'identité" /></SelectTrigger>
              <SelectContent>
                {PIECE_IDENTITE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              data-testid="rep-piece-identite-numero"
              disabled={!canEdit}
              placeholder="Numéro de la pièce d'identité"
              value={data.piece_identite_numero}
              onChange={(e) => setData({ ...data, piece_identite_numero: e.target.value })}
            />
            <Input
              data-testid="rep-piece-identite-delivree-le"
              disabled={!canEdit}
              placeholder="Date de délivrance (JJ/MM/AAAA)"
              value={data.piece_identite_delivree_le}
              onChange={(e) => setData({ ...data, piece_identite_delivree_le: e.target.value })}
            />
            <Input
              data-testid="rep-piece-identite-lieu-delivrance"
              disabled={!canEdit}
              placeholder="Lieu de délivrance (facultatif)"
              value={data.piece_identite_lieu_delivrance}
              onChange={(e) => setData({ ...data, piece_identite_lieu_delivrance: e.target.value })}
            />
          </div>
        </div>

        <p className="font-semibold text-sm mb-2">État du produit</p>
        <div className="flex flex-wrap gap-4 mb-5">
          {ETAT_OPTIONS.map((o) => (
            <label key={o.key} className="flex items-center gap-2 text-sm">
              <Checkbox data-testid={`rep-etat-${o.key}`} disabled={!canEdit} checked={!!data.etat_produit[o.key]} onCheckedChange={() => toggleEtat(o.key)} />
              {o.label}
            </label>
          ))}
        </div>

        <p className="font-semibold text-sm mb-2">Tests à effectuer</p>
        <div className="mb-3">
          <DiagnosticTestsEditor tests={data.tests} onChange={(tests) => setData({ ...data, tests })} disabled={!canEdit} />
        </div>
        <Input data-testid="rep-batterie" disabled={!canEdit} type="number" placeholder="Batterie %" className="w-32 mb-6" value={data.batterie_pourcentage} onChange={(e) => setData({ ...data, batterie_pourcentage: e.target.value })} />

        <p className="font-semibold text-sm mb-2">Schéma - cliquez pour marquer un défaut</p>
        {canEdit && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-3 no-print">
            <div className="flex rounded-md overflow-hidden border">
              <button
                type="button"
                data-testid="reprise-mark-type-casse"
                onClick={() => setMarkType("casse")}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium ${markType === "casse" ? "bg-red-600 text-white" : "bg-muted text-muted-foreground"}`}
              >
                <X size={14} />Cassé
              </button>
              <button
                type="button"
                data-testid="reprise-mark-type-abime"
                onClick={() => setMarkType("abime")}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium ${markType === "abime" ? "bg-red-600 text-white" : "bg-muted text-muted-foreground"}`}
              >
                <Circle size={14} />Abîmé
              </button>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 text-xs"
              data-testid="reprise-mark-undo"
              disabled={!data.defauts_marks.length}
              onClick={undoLastMark}
            >
              <Undo2 size={14} />Annuler le dernier repère
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 text-xs"
              data-testid="reprise-mark-clear"
              disabled={!data.defauts_marks.length}
              onClick={clearMarks}
            >
              <Eraser size={14} />Tout effacer
            </Button>
          </div>
        )}
        <p className="text-xs text-center text-muted-foreground mb-2">
          Cliquez sur le schéma pour ajouter un repère ({markType === "casse" ? "✕ cassé" : "○ abîmé"}). Cliquez sur un repère existant pour le supprimer.
        </p>
        <div className="flex gap-4 sm:gap-8 justify-center mb-6">
          <div>
            <p className="text-xs text-center mb-1 text-muted-foreground">Avant</p>
            <PhoneDiagram
              marks={data.defauts_marks}
              onAdd={canEdit ? addMark : () => {}}
              onRemove={canEdit ? removeMark : undefined}
              markType={markType}
              face="avant"
            />
          </div>
          <div>
            <p className="text-xs text-center mb-1 text-muted-foreground">Arrière</p>
            <PhoneDiagram
              marks={data.defauts_marks}
              onAdd={canEdit ? addMark : () => {}}
              onRemove={canEdit ? removeMark : undefined}
              markType={markType}
              face="arriere"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-semibold">Remarques</label>
          <span className="text-xs text-muted-foreground" data-testid="rep-remarques-counter">
            {REMARQUES_MAX_LENGTH - (data.remarques?.length || 0)} caractère(s) restant(s)
          </span>
        </div>
        <Textarea
          data-testid="rep-remarques"
          disabled={!canEdit}
          className="mb-4 h-24"
          maxLength={REMARQUES_MAX_LENGTH}
          value={data.remarques}
          onChange={(e) => setData({ ...data, remarques: e.target.value })}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <div className="flex items-center justify-end mb-1">
              <span className="text-xs text-muted-foreground" data-testid="rep-piece-counter">
                {PIECE_A_REMPLACER_MAX_LENGTH - (data.piece_a_remplacer?.length || 0)} caractère(s) restant(s)
              </span>
            </div>
            <Input
              data-testid="rep-piece"
              disabled={!canEdit}
              placeholder="Pièce à remplacer"
              maxLength={PIECE_A_REMPLACER_MAX_LENGTH}
              value={data.piece_a_remplacer}
              onChange={(e) => setData({ ...data, piece_a_remplacer: e.target.value })}
            />
          </div>
          <Input data-testid="rep-offre" disabled={!canEdit} type="number" placeholder="Offre de rachat (€)" value={data.offre_rachat} onChange={(e) => setData({ ...data, offre_rachat: Number(e.target.value) })} />
        </div>

        <p className="text-xs text-muted-foreground mb-4">{DISCLAIMER}</p>

        {!identityComplete && canEdit && (
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-3" data-testid="rep-identity-incomplete-warning">
            Complétez l'identité du vendeur (livre de police, ci-dessus) avant de faire signer la fiche.
          </p>
        )}

        {/* La mention manuscrite et la signature ne sont plus saisies
            directement sur la page : elles se font via le bouton
            "Enregistrer et signer" ci-dessous, qui ouvre une fenêtre dédiée
            en deux étapes. Une fois la fiche clôturée, on affiche ici un
            aperçu en lecture seule des deux images enregistrées. */}
        {isCloturee && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2" data-testid="reprise-signatures-preview">
            <div>
              <label className="text-sm font-semibold block mb-1">Bon pour accord</label>
              <div className="w-full h-36 bg-slate-50 dark:bg-zinc-800 border border-border rounded-lg flex items-center justify-center overflow-hidden">
                {data.mention_bon_pour_accord ? (
                  <img src={data.mention_bon_pour_accord} alt="Mention bon pour accord" className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1">Signature</label>
              <div className="w-full h-36 bg-slate-50 dark:bg-zinc-800 border border-border rounded-lg flex items-center justify-center overflow-hidden">
                {data.signature_data ? (
                  <img src={data.signature_data} alt="Signature" className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </div>
            </div>
          </div>
        )}
        {isCloturee && (
          <p className="text-xs text-muted-foreground mt-2">
            Document clôturé : la mention et la signature déjà apposées ne sont plus modifiables.
          </p>
        )}

        <DocumentImagesGallery
          module="reprise" itemId={isNew ? null : id} isCloturee={isCloturee} canEdit={hasEditPerm}
          stagedFiles={stagedImages} onStagedFilesChange={setStagedImages}
        />

        {!isNew && isCloturee && !data.annule && Number(data.offre_rachat) > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="reprise-vente-block">
            {data.paiement ? (
              <>
                <p className="text-sm text-emerald-600" data-testid="reprise-paiement-ok">
                  ✓ Rachat payé en espèces : sortie de {Number(data.paiement.montant).toFixed(2)} € (caisse)
                </p>
                <Button variant="outline" className="gap-2" onClick={openTicketRachat} data-testid="reprise-ticket-rachat-button">
                  <Printer size={16} />Ticket de rachat
                </Button>
              </>
            ) : data.vente_ticket_numero ? (
              <p className="text-sm text-emerald-600" data-testid="reprise-vente-numero">✓ Passée en caisse : vente N° {data.vente_ticket_numero}</p>
            ) : hasPerm(user, "caisse", "access") && (
              <>
                <Button className="gap-2" onClick={payerRachat} data-testid="reprise-payer-rachat-button">
                  Payer le rachat en espèces (sortie de {Number(data.offre_rachat).toFixed(2)} €)
                </Button>
                <Button variant="outline" className="gap-2" data-testid="reprise-creer-vente-button"
                  onClick={() => navigate("/caisse", { state: { fromReprise: {
                    id: data.id, numero: data.numero, client_id: data.client_id, client_nom: data.client_nom,
                    client_adresse: data.client_adresse, client_email: data.client_email, offre_rachat: data.offre_rachat,
                  } } })}>
                  Déduire d'une vente
                </Button>
              </>
            )}
          </div>
        )}

        {!isNew && (
          <div className="mt-4">
            {data.article_stock_id ? (
              <p className="text-sm text-emerald-600 flex items-center gap-1.5" data-testid="reprise-deja-ajoute-stock">
                ✓ Ce téléphone a déjà été ajouté au stock.
              </p>
            ) : hasEditPerm && (
              <Button variant="outline" className="gap-2" onClick={() => setStockDialogOpen(true)} data-testid="reprise-ajouter-au-stock-button">
                <PackagePlus size={16} />Ajouter au stock
              </Button>
            )}
          </div>
        )}

        <AjouterAuStockDialog
          open={stockDialogOpen}
          onClose={() => setStockDialogOpen(false)}
          reprise={data}
          onAjoute={(article) => setData((d) => ({ ...d, article_stock_id: article.id }))}
        />

        <div className="flex gap-3 mt-6 no-print">
          {canEdit && <Button data-testid="reprise-save-button" onClick={save} className="gap-2"><Save size={16} />Enregistrer</Button>}
          {canEdit && (
            <Button
              data-testid="reprise-save-and-sign-button"
              variant="outline"
              onClick={openSignFlow}
              disabled={!identityComplete || signBusy}
              className="gap-2"
            >
              <PenLine size={16} />Enregistrer et signer
            </Button>
          )}
          {!isNew && (
            <a href={`${api.defaults.baseURL}/reprises/${id}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="reprise-pdf-button" className="gap-2"><Printer size={16} />PDF</Button>
            </a>
          )}
          {!isNew && (
            <Button variant="outline" className="gap-2" onClick={openMailDialog} data-testid="reprise-send-email-button">
              <Mail size={16} />Envoyer par e-mail
            </Button>
          )}
          {!isNew && (
            <a href={`${api.defaults.baseURL}/reprises/${id}/etiquettes`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="reprise-etiquettes-button" className="gap-2"><Tag size={16} />Étiquette ID</Button>
            </a>
          )}
        </div>
      </div>

      <Dialog open={signOpen} onOpenChange={closeSignDialog}>
        <DialogContent data-testid="reprise-sign-dialog" className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {signStep === "mention" ? "Bon pour accord" : "Signature"}
            </DialogTitle>
          </DialogHeader>

          {signStep === "mention" ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Le client écrit lui-même « Bon pour accord » dans le cadre ci-dessous.
              </p>
              {/* `key` explicite : sans elle, React réutilise la même instance
                  de <SignaturePad> (même type de composant au même endroit de
                  l'arbre) en passant à l'étape signature, donc le <canvas>
                  n'est jamais réellement recréé — le dessin de la mention
                  restait affiché à l'écran de signature au lieu d'être vide. */}
              <SignaturePad
                key="mention-pad"
                ref={mentionSigRef}
                testId="reprise-mention-pad"
                heightClass="h-64 sm:h-72"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Faites signer le client dans le cadre ci-dessous.
              </p>
              <SignaturePad
                key="signature-pad"
                ref={sigRef}
                testId="reprise-signature-pad"
                heightClass="h-64 sm:h-72"
              />
            </div>
          )}

          <DialogFooter className="gap-2">
            {signStep === "signature" && (
              <Button
                type="button"
                variant="ghost"
                className="gap-2"
                data-testid="reprise-sign-back-button"
                onClick={() => setSignStep("mention")}
                disabled={signBusy}
              >
                <ArrowLeft size={15} />Retour
              </Button>
            )}
            {signStep === "mention" ? (
              <Button data-testid="reprise-sign-validate-mention-button" onClick={validateMentionStep} disabled={signBusy} className="gap-2">
                Valider
              </Button>
            ) : (
              <Button data-testid="reprise-sign-finalize-button" onClick={finalizeSignature} disabled={signBusy} className="gap-2">
                {signBusy ? "Clôture..." : "Valider et clôturer"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent data-testid="reprise-mail-dialog">
          <DialogHeader><DialogTitle>Envoyer la fiche par e-mail</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Adresse e-mail du client" value={mailTo} onChange={(e) => setMailTo(e.target.value)} data-testid="reprise-mail-to" />
            <Input placeholder="Objet" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} data-testid="reprise-mail-subject" />
            <Textarea className="h-32" placeholder="Message" value={mailMessage} onChange={(e) => setMailMessage(e.target.value)} data-testid="reprise-mail-message" />
            <p className="text-xs text-muted-foreground">Le PDF sera joint automatiquement. L'e-mail sera envoyé depuis l'adresse configurée pour votre boutique (Admin &gt; Boutiques &amp; Dépôts).</p>
          </div>
          <DialogFooter>
            <Button onClick={sendMail} disabled={sending} className="gap-2" data-testid="reprise-mail-send-button">
              <Mail size={16} />{sending ? "Envoi..." : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
